import {computed, Injectable, signal} from '@angular/core';
import {environment} from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {

  registerUrl = '';
  logoutUrl = '';
  profileUrl = '';
  eNotificationExistingUsersLink = '';
  private readonly backUrlStorageKey = 'app:lastNonLoginBackUrl';

  tabName = signal('');
  showQaBanner = signal(false);
  private hostnameSignal = signal(window.location.hostname);

  isQA = computed(() => this.hostnameSignal().includes('qa'));

  constructor() {
    const ssoUrl = environment.ssoBase;
    if (ssoUrl) {
      this.registerUrl = ssoUrl + '/register';
      this.logoutUrl = '/logout';
      this.profileUrl = ssoUrl + '/myprofile';
      this.eNotificationExistingUsersLink = this.profileUrl + '/homeowner-alerts';
    }

    this.tabName.set(`Assessor ${environment.friendlyName}`);
    this.showQaBanner.set(environment.showQaBanner);
  }

  public rememberNavigatedUrl(url: string): void {
    const absoluteUrl = this.toAbsoluteUrl(url);
    if (this.isUsableBackUrl(absoluteUrl)) {
      sessionStorage.setItem(this.backUrlStorageKey, absoluteUrl);
    }
  }

  public getSsoRegisterUrl(): string {
    return this.withBackUrl(this.registerUrl);
  }

  public getSsoProfileUrl(): string {
    return this.withBackUrl(this.profileUrl);
  }

  public getExistingUsersUrl(): string {
    return this.withBackUrl(this.eNotificationExistingUsersLink);
  }

  private withBackUrl(baseUrl: string): string {
    const currentUrl = this.getBestBackUrl();
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}backUrl=${encodeURIComponent(currentUrl)}`;
  }

  private getBestBackUrl(): string {
    const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (this.isUsableBackUrl(currentUrl)) {
      return currentUrl;
    }

    const storedUrl = sessionStorage.getItem(this.backUrlStorageKey);
    if (storedUrl && this.isUsableBackUrl(storedUrl)) {
      return storedUrl;
    }

    return currentUrl;
  }

  private isUsableBackUrl(url: string): boolean {
    if (!url || /^(null|undefined)$/i.test(url.trim())) {
      return false;
    }

    try {
      const parsed = new URL(url, window.location.origin);
      return /^https?:$/i.test(parsed.protocol) && !this.isLoginUrl(parsed.href);
    } catch {
      return false;
    }
  }

  private isLoginUrl(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname === '/login';
    } catch {
      return false;
    }
  }

  private toAbsoluteUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return `${window.location.origin}${normalized}`;
  }
}
